/*
 * Copyright (c) 2019-2021 LogMeIn
 * All Rights Reserved Worldwide.
 *
 * THIS PROGRAM IS CONFIDENTIAL AND PROPRIETARY TO LOGMEIN
 * AND CONSTITUTES A VALUABLE TRADE SECRET.
 */

import { getLocale } from './TranslationService';
import { isAndroid } from './ClientService';
import { Product } from './IndexPageReaderService';
import { getCurrentUrl } from './NavigatorService';
import { getEnvConfig } from '../context/SessionContext';

export const successFragment = '#s';

export const installGoogleChromeUrl = 'https://www.google.com/chrome/';
export const installMicrosoftEdgeUrl = 'https://www.microsoft.com/edge';

export const buildInstantJoinUrl = (
  meetingId: string | undefined,
  sessionTrackingId: string
): string => {
  const query = meetingId
    ? `/?meetingId=${meetingId}&sessionTrackingId=${sessionTrackingId}`
    : '/'; //Could only transfer sessionTId, but IJ Service cant handle it (shows it in browser url)
  return getEnvConfig().instantJoinBaseUrl + query;
};

export const buildInstantJoinDisplayUrl = (meetingId?: string): string => {
  return `${getEnvConfig().instantJoinBaseUrl}/${meetingId ? meetingId : ''}`;
};

export const buildGoToOpenerJoinUrl = (launcherToken: string): string => {
  return `gotoopener://${
    getEnvConfig().jwtResolutionDomainBasePath
  }/${launcherToken}`;
};

export const buildNativeDownloadUrl = (launcherToken: string): string => {
  return `${getEnvConfig().jwtOpenerPatchingBaseUrl}/${launcherToken}`;
};

export const buildStatusPollingUrl = (
  abbreviatedLauncherToken: string,
  lastStatus?: string,
  launchStatusTimestamp?: string
): string => {
  const baseUrl = `${
    getEnvConfig().launcherStatusBaseUrl
  }/launch-status?token=${abbreviatedLauncherToken}`;
  const lastStatusParam = lastStatus
    ? `&lastReceivedStatus=${encodeURI(lastStatus)}`
    : '';
  const launchStatusTimestampParam = launchStatusTimestamp
    ? `&lastReceivedTimestamp=${launchStatusTimestamp}`
    : '';
  return `${baseUrl}${lastStatusParam}${launchStatusTimestampParam}`;
};

export const buildTelemetryUrl = (telemetryToken: string): string => {
  return `${
    getEnvConfig().launcherStatusBaseUrl
  }/telemetry/helper?token=${telemetryToken}`;
};

export const buildFallbackLauncherUrl = (launcherToken: string): string => {
  return `${
    getEnvConfig().launcherStatusBaseUrl
  }/launch.html?token=${launcherToken}`;
};

export const removeProtocolPrefixFromUrl = (
  urlWithProtocolPrefix: string
): string => {
  return urlWithProtocolPrefix.replace(/(^\w+:|^)\/\//, '');
};

export const buildReInstallEPUrl = (
  meetingId: string = '',
  sessionTrackingId: string
): string => {
  return `${getOriginFromUrl()}/install/${meetingId}?sessionTrackingId=${sessionTrackingId}`;
};

export const buildEngagementServiceUrl = (
  meetingId: string | undefined
): string => {
  const product = 'g2m';
  const locale = getLocale();
  return `${getEnvConfig().engagementServiceBaseUrl}/meeting/${
    meetingId || ''
  }?product=${product}&locale=${locale}`;
};

export const removeQueryParametersAndFragmentsFromUrl = (): boolean => {
  let hasParameterRemoved = false;
  const url = window.location;
  if (url.href.includes('?') || url.href.includes('#')) {
    const pathWithoutHashAndQueries = extractPathFromUrl(url);
    window.history.replaceState(
      {},
      document.title,
      url.origin + pathWithoutHashAndQueries
    );
    hasParameterRemoved = true;
  }
  return hasParameterRemoved;
};

export const extractPathFromUrl = (url: Location): string => {
  if (url.hash === successFragment) {
    return url.pathname.concat(successFragment);
  }
  return url.pathname;
};

export const getOriginFromUrl = () => {
  return new URL(getCurrentUrl()).origin;
};

export const addSuccessFragment = (): void => {
  window.history.replaceState(
    {},
    document.title,
    window.location.origin + window.location.pathname + successFragment
  );
};

export const hasSuccessFragment = (): boolean => {
  return window.location.hash === successFragment;
};

export const addTrackingInformationSuffix = (url: string) => {
  const trackingParam = 'cid=g2mjoinflow';
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${trackingParam}`;
};

export const buildMobileJoinScheme = (
  meetingId: string | undefined,
  sessionTrackingId: string
) => {
  const prefix = isAndroid ? 'COL-G2M-2' : 'lmi-g2m';
  const meetingIdString = meetingId ? '&MeetingID=' + meetingId : '';
  return `${prefix}://BRLaunch?Action=Join${meetingIdString}&sessionTrackingId=${sessionTrackingId}&Product=g2m`;
};

const mobileAppStoreUrls = {
  android: {
    g2m: 'https://play.google.com/store/apps/details?id=com.gotomeeting',
    g2t: 'https://play.google.com/store/apps/details?id=com.logmein.gototraining',
    g2w: 'https://play.google.com/store/apps/details?id=com.logmein.gotowebinar',
  },
  ios: {
    g2m: 'https://apps.apple.com/de/app/gotomeeting/id1239774423',
    g2t: 'https://apps.apple.com/us/app/gototraining/id1245324531',
    g2w: 'https://apps.apple.com/us/app/gotowebinar/id1244921300',
  },
};

export const getIosAppUrl = (product: Product) => {
  return mobileAppStoreUrls.ios[product];
};

export const getAndroidAppUrl = (product: Product) => {
  return mobileAppStoreUrls.android[product];
};
